import { hashValue } from '@ggs/utils'

/**
 * Settings action types.
 * @enum {string}
 */
export const settingsActionType = {
  CLEAR_NOTICE: 'CLEAR_NOTICE',
  SET_CURRENT_LANGUAGE: 'SET_CURRENT_LANGUAGE',
  SET_CURRENT_COUNTRY: 'SET_CURRENT_COUNTRY',
  SET_CURRENT_STATE_PROVINCE: 'SET_CURRENT_STATE_PROVINCE',
  SAVE_ENTITY_OPERATION: 'SAVE_ENTITY_OPERATION',
  CLEAN_ENTITY_OPERATION: 'CLEAN_ENTITY_OPERATION',
}

/**
 * @typedef {Object} SetCurrentLanguageActionPayload
 * @property {String} language
 */

/**
 * @typedef {Object} SetCurrentLanguageAction
 * @property {settingsActionType} type Action type key
 * @property {SetCurrentLanguageActionPayload} payload Action input data
 */

/**
 * Set current market action.
 *
 * @param {String} language CL market ID
 * @return {SetCurrentLanguageAction}
 */
export const setCurrentLanguage = (language) => {
  return {
    type: settingsActionType.SET_CURRENT_LANGUAGE,
    payload: {
      language,
    },
  }
}

/**
 * @typedef {Object} ClearNoticeAction
 * @property {settingsActionType} type Action type key
 * @property {String} payload
 */

/**
 * Action to clear specific UI alert by ID.
 * @param {String} label The alert ID to clear
 * @return {ClearNoticeAction} Action object.
 */
export const clearNotice = (label) => {
  return {
    type: settingsActionType.CLEAR_NOTICE,
    payload: hashValue(label),
  }
}

/**
 * @typedef {Object} SetCurrentCountryActionPayload
 * @property {String} country
 */

/**
 * @typedef {Object} SetCurrentCountryAction
 * @property {settingsActionType} type Action type key
 * @property {SetCurrentCountryActionPayload} payload Action input data
 */

/**
 * Set current country action.
 *
 * @param {String} country
 * @return {SetCurrentCountryAction}
 */
export const setCurrentCountry = (country) => {
  return {
    type: settingsActionType.SET_CURRENT_COUNTRY,
    payload: {
      country,
    },
  }
}

/**
 * @typedef {Object} SetCurrentStateProvinceActionPayload
 * @property {String} stateProvince
 */

/**
 * @typedef {Object} SetCurrentStateProvinceAction
 * @property {settingsActionType} type Action type key
 * @property {SetCurrentStateProvinceActionPayload} payload Action input data
 */

/**
 * Set current state/province action.
 *
 * @param {String} stateProvince
 * @return {SetCurrentStateProvinceAction}
 */
export const setCurrentStateProvince = (stateProvince) => {
  return {
    type: settingsActionType.SET_CURRENT_STATE_PROVINCE,
    payload: {
      stateProvince,
    },
  }
}

/**
 * @typedef {Object<string, any>} EntityAttributes
 */

/**
 * @typedef {Object} EntityOperationPayload
 * @property {string} operationId Unique operation ID
 * @property {string|null} operation Unique operation ID
 * @property {EntityAttributes|null} attributes Entity attributes
 */

/**
 * @typedef {Object} EntityOperationAction
 * @property {settingsActionType} type Action type key
 * @property {EntityOperationPayload} payload Action input data
 */

/**
 * Set form values.
 *
 * @param {string} operationId Unique operation ID
 * @param {string} operation Entity operation to process
 * @param {EntityAttributes} attributes Entity attributes
 * @return {EntityOperationAction}
 */
export const saveEntityOperation = (operationId, operation, attributes) => {
  return {
    type: settingsActionType.SAVE_ENTITY_OPERATION,
    payload: {
      operationId: operationId,
      operation,
      attributes,
    },
  }
}

/**
 * Clean entity operation.
 *
 * @param {string} operationId
 * @return {EntityOperationAction}
 */
export const cleanEntityOperation = (operationId) => {
  return {
    type: settingsActionType.CLEAN_ENTITY_OPERATION,
    payload: {
      operationId,
      operation: null,
      attributes: null,
    }
  }
}
